import React, { PropsWithChildren, useMemo } from 'react';

import { Button, ButtonProps } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { GestureResponderEvent } from 'react-native';

import { ActionButtonRadialGradient } from 'components/app-takeover/action-button-radial-gradient';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import useTraceLoading from 'hooks/use-trace-loading';
import { logButtonClick, logEvent } from 'state/amplitude';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/amplitude/constants';
import { checkForOSPrompts } from 'state/location/routes';
import { ACTION_BUTTON_MAX_FONT_SIZE_MULTIPLIER } from 'styles/constants/fonts';
import { isWeb } from 'utils/environment';
import { getTextFromChildren } from 'utils/get-text-from-children';
import { hapticImpact } from 'utils/haptic';

import {
  ActionButtonSizes,
  ActionButtonVariants,
  IActionButtonProps,
  IconElementProp,
} from './types';

const resizeIcon = (icon: IconElementProp | undefined, isSmallButton: boolean) =>
  icon && React.cloneElement(icon, { size: isSmallButton ? '3' : '4' });

const ActionButton = ({
  children,
  disabled,
  eventAttributes = {},
  eventName,
  eventType,
  skipLogEvent = false,
  isLoading = false,
  onPress,
  onNonVisualPress,
  to,
  linkPath,
  perceptible,
  hapticFeedbackStyle,
  variant,
  color,
  tabIndex = 0,
  fullWidth,
  rightIcon,
  leftIcon,
  startIcon,
  endIcon,
  width,
  noLogOnLongLoading,
  size = ActionButtonSizes.LARGE,
  keepIconSize,
  ...rest
}: PropsWithChildren<IActionButtonProps>) => {
  const isImperceptible = !perceptible && disabled;
  const { navigate } = useRouter();
  const {
    primaryActionButton: takeoverPrimaryActionButton,
    primaryActionButtonRadialGradient,
  } = useAppTakeover();
  const handleClick = (e: GestureResponderEvent) => {
    if (isLoading) {
      e.preventDefault();
      return;
    }
    if (isImperceptible) {
      e.preventDefault();
      return;
    } else if (perceptible && disabled) {
      e.preventDefault();
      void onNonVisualPress?.(e);
      return;
    }

    if (!skipLogEvent) {
      try {
        if (eventName && eventName !== CustomEventNames.BUTTON_CLICK) {
          logEvent(eventName, eventType || EventTypes.Navigation, {
            Name: eventName,
            ...eventAttributes,
          });
        } else {
          logButtonClick({
            attributes: {
              ...eventAttributes,
              component: ClickEventComponentNames.BUTTON,
              Name: getTextFromChildren(children),
              name: getTextFromChildren(children),
            },
          });
        }
      } catch (_) {
        // Nothing to do here
      }
    }

    if (hapticFeedbackStyle) {
      hapticImpact({ style: hapticFeedbackStyle });
    }

    if (onPress) {
      onPress(e);
    }

    const navigationPath = linkPath ?? to;

    if (navigationPath) {
      navigate(navigationPath);
    }
  };

  const buttonName = eventName || getTextFromChildren(children) || rest.testID;
  const isLoadingOrLoggingDisabled = noLogOnLongLoading ? false : isLoading;
  useTraceLoading({
    name: buttonName,
    isLoading: isLoadingOrLoggingDisabled,
  });

  const focusable = !(isLoading || isImperceptible || tabIndex === -1);
  const isSmallButton = size === ActionButtonSizes.SMALL;

  const iconProps: Partial<ButtonProps> = useMemo(
    () => ({
      leftIcon: keepIconSize ? leftIcon : resizeIcon(leftIcon, isSmallButton),
      rightIcon: keepIconSize ? rightIcon : resizeIcon(rightIcon, isSmallButton),
      startIcon: keepIconSize ? startIcon : resizeIcon(startIcon, isSmallButton),
      endIcon: keepIconSize ? endIcon : resizeIcon(endIcon, isSmallButton),
    }),
    [keepIconSize, endIcon, isSmallButton, leftIcon, rightIcon, startIcon]
  );

  const commonProps: Partial<ButtonProps> = {
    ...rest,
    ...iconProps,
    _text: {
      color,
      maxFontSizeMultiplier: ACTION_BUTTON_MAX_FONT_SIZE_MULTIPLIER,
      textAlign: 'center',
      ...rest?._text,
    },
    isDisabled: !!disabled,
    onPress: handleClick,
    isLoading,
    focusable,
    width: fullWidth ? 'full' : width,
    size: isSmallButton ? 'sm' : 'lg',
  };

  const variantProps: Partial<ButtonProps> = useMemo(() => {
    switch (variant) {
      case ActionButtonVariants.BOX_SHADOW_PRIMARY:
        return {
          shadow: '4',
        };
      case ActionButtonVariants.BOX_SHADOW_INVERSE:
        return {
          shadow: '4',
          variant: 'solid-reversed',
        };
      case ActionButtonVariants.TEXT_ONLY:
        return {
          variant: 'ghost',
          color,
        };
      case ActionButtonVariants.INVERSE:
        return {
          variant: 'solid-reversed',
        };
      case ActionButtonVariants.OUTLINE:
        return {
          variant: 'outline',
        };
      case ActionButtonVariants.PRIMARY_TAKEOVER:
        return takeoverPrimaryActionButton;
      case ActionButtonVariants.PRIMARY:
      default:
        return {};
    }
  }, [color, takeoverPrimaryActionButton, variant]);

  const shouldHaveRadialGradient =
    variant === ActionButtonVariants.PRIMARY_TAKEOVER &&
    primaryActionButtonRadialGradient &&
    !isWeb &&
    !disabled;

  return (
    <Button {...commonProps} {...variantProps} dd-action-name={buttonName}>
      {shouldHaveRadialGradient && (
        <ActionButtonRadialGradient config={primaryActionButtonRadialGradient} />
      )}
      {children}
    </Button>
  );
};

export const ActionLink = ({
  children,
  to,
  linkPath,
  fullWidth = false,
  onPress,
  ...props
}: React.PropsWithChildren<IActionButtonProps & { to: string }>) => {
  const { navigate } = useRouter();
  const isOSLink = to?.startsWith('/OS/');
  const navigationPath = linkPath ?? to;
  const handlePress = (e: GestureResponderEvent) => {
    if (isOSLink) {
      checkForOSPrompts(to);
    } else if (navigationPath) {
      navigate(navigationPath);
    }

    if (onPress) {
      onPress(e);
    }
  };
  return (
    <ActionButton onPress={handlePress} fullWidth={fullWidth} {...props}>
      {children}
    </ActionButton>
  );
};

export default ActionButton;

export { ActionButtonVariants, ActionButtonSizes } from './types';
export type { IActionButtonProps, IActionLinkProps } from './types';
